import React, { useState, useEffect } from 'react';
import { consistencySubscriptionPackages, consistencySubscriptionStatus, subscribeToPackage } from '../services/AIServices';
import toast from 'react-hot-toast';
import { FaCrown, FaCheck, FaSpinner, FaRocket, FaStar, FaMobileAlt, FaCopy } from 'react-icons/fa';
import ConsistencyControl from '../AI-COMPONENT/ConsistencyControl';

const PremiumPage = () => {
    const [packages, setPackages] = useState([]);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [packagesRes, statusRes] = await Promise.all([
                consistencySubscriptionPackages(),
                consistencySubscriptionStatus()
            ]);

            if (packagesRes?.success) {
                setPackages(packagesRes.packages || []);
                setPaymentInfo(packagesRes.paymentInfo || null);
            }

            if (statusRes?.success) {
                setSubscriptionStatus(statusRes);
            }
        } catch (error) {
            console.error('Error fetching premium data:', error);
            toast.error('Failed to load premium packages');
        } finally {
            setLoading(false);
        }
    };

    const handleShowPayment = (pkg) => {
        setSelectedPackage(pkg);
        setShowPaymentModal(true);
    };

    const handleCopyTillNumber = (tillNumber) => {
        navigator.clipboard.writeText(tillNumber);
        toast.success('Till Number copied to clipboard!');
    };

    const handleCloseModal = () => {
        setShowPaymentModal(false);
        setSelectedPackage(null);
    };

    const getPackageIcon = (index) => {
        const icons = [FaRocket, FaStar, FaCrown];
        return icons[index] || FaCrown;
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FaSpinner className="animate-spin text-4xl text-blue-600" />
            </div>
        );
    }

    return (
        <div className="py-12 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-lg mb-4">
                        <FaCrown className="text-4xl text-white" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                        Premium Consistency AI
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        Unlock the power of AI-driven trading with strategic consistency tracking that avoids peak exhaustion
                    </p>
                </div>

                {/* Subscription Status */}
                {subscriptionStatus?.hasSubscription && subscriptionStatus?.subscription && (
                    <div className="mb-8 max-w-2xl mx-auto">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg text-white">
                            <div className="flex items-center gap-3">
                                <FaCheck className="text-2xl" />
                                <div>
                                    <h3 className="text-lg font-bold">Active Premium Subscription</h3>
                                    <p className="text-sm opacity-95">
                                        <strong>Time Left:</strong> {(() => {
                                            const now = new Date();
                                            const endDate = new Date(subscriptionStatus.subscription.end_date);
                                            const diffTime = endDate - now;
                                            
                                            if (diffTime <= 0) return 'Expired';
                                            
                                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                            const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                            const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
                                            
                                            const parts = [];
                                            if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
                                            if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
                                            if (diffMinutes > 0 && diffDays === 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
                                            
                                            return parts.length > 0 ? parts.join(', ') : 'Less than a minute';
                                        })()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Consistency Control - Show when user has active subscription */}
                {subscriptionStatus?.hasSubscription && (
                    <div className="mb-12 max-w-5xl mx-auto">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {/* Header with Premium Badge */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                            <FaCrown className="text-2xl text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-white">Consistency AI Control</h2>
                                            <p className="text-sm text-blue-100">Your premium trading intelligence system</p>
                                        </div>
                                    </div>
                                    <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                                        <span className="text-white text-xs font-semibold">PREMIUM</span>
                                    </div>
                                </div>
                            </div>

                            {/* Instructions Alert */}
                            <div className="mx-6 -mt-3 mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                                            💡 Optimal Trading Strategy
                                        </h4>
                                        <p className="text-sm text-amber-800 dark:text-amber-200">
                                            For best results, add consistency values of <strong>4 or higher</strong>. Our AI targets the optimal 4-5 trend strength range, avoiding dangerous peak consistency where most traders lose money.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Consistency Control Component */}
                            <div className="px-6 pb-6">
                                <div className="flex justify-center">
                                    <ConsistencyControl />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Packages Grid - Only show if no active subscription */}
                {!subscriptionStatus?.hasSubscription && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {packages.map((pkg, index) => {
                        const Icon = getPackageIcon(index);
                        const isPopular = pkg.name.includes('Weekly');
                        const isBestValue = pkg.name.includes('Monthly');

                        return (
                            <div
                                key={pkg.id}
                                className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-105 ${
                                    isPopular ? 'ring-4 ring-blue-500 dark:ring-blue-400' : ''
                                } ${isBestValue ? 'ring-4 ring-purple-500 dark:ring-purple-400' : ''}`}
                            >
                                {/* Popular Badge */}
                                {isPopular && (
                                    <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        POPULAR
                                    </div>
                                )}
                                {isBestValue && (
                                    <div className="absolute top-4 right-4 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        BEST VALUE
                                    </div>
                                )}

                                {/* Header */}
                                <div className={`p-6 ${
                                    isPopular ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                    isBestValue ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                                    'bg-gradient-to-br from-gray-500 to-gray-600'
                                } text-white`}>
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                                            <Icon className="text-4xl" />
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-center mb-2">{pkg.name}</h3>
                                    <div className="text-center">
                                        <span className="text-4xl font-bold">{formatPrice(pkg.price)}</span>
                                        <span className="text-sm opacity-90 ml-2">/ {pkg.duration_days} {pkg.duration_days === 1 ? 'day' : 'days'}</span>
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="p-6">
                                    <ul className="space-y-3 mb-6">
                                        <li className="flex items-start gap-2">
                                            <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">AI-powered consistency tracking</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Optimal 4-5 trend strength entries</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Automated execution & monitoring</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Real-time market analysis</span>
                                        </li>
                                        {pkg.duration_days >= 7 && (
                                            <li className="flex items-start gap-2">
                                                <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">Priority support</span>
                                            </li>
                                        )}
                                    </ul>

                                    {/* Subscribe Button */}
                                    <button
                                        onClick={() => handleShowPayment(pkg)}
                                        disabled={subscriptionStatus?.hasSubscription}
                                        className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
                                            subscriptionStatus?.hasSubscription
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : isPopular
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-blue-500/50'
                                                : isBestValue
                                                ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-purple-500/50'
                                                : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                                        } ${subscriptionStatus?.hasSubscription ? '' : 'hover:scale-105 active:scale-95'}`}
                                    >
                                        {subscriptionStatus?.hasSubscription ? (
                                            'Already Subscribed'
                                        ) : (
                                            <>
                                                <FaMobileAlt className="inline mr-2" />
                                                Get Payment Details
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                )}

                {/* Features Section - Only show if no active subscription */}
                {!subscriptionStatus?.hasSubscription && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                        Why Premium Consistency AI?
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center p-4 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-4">
                                <FaRocket className="text-3xl text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Smart Entry Points</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Enter trades at optimal 4-5 consistency levels, avoiding dangerous peaks
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center p-4 bg-purple-100 dark:bg-purple-900/30 rounded-xl mb-4">
                                <FaStar className="text-3xl text-purple-600 dark:text-purple-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI Precision</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Advanced algorithms analyze market trends in real-time for perfect timing
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center p-4 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
                                <FaCrown className="text-3xl text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Automated Execution</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Zero human emotion, split-second execution before market exhaustion
                            </p>
                        </div>
                    </div>
                </div>
                )}

                {/* Payment Modal */}
                {showPaymentModal && selectedPackage && (
                    <div 
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
                        onClick={handleCloseModal}
                    >
                        <div 
                            className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 text-white sticky top-0 z-10">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="text-xl font-bold">M-Pesa Payment</h3>
                                    <button
                                        onClick={handleCloseModal}
                                        className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-all hover:scale-110"
                                        title="Close"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <p className="text-sm opacity-90">{selectedPackage.name}</p>
                            </div>

                            {/* Modal Body */}
                            <div className="p-4 space-y-4">
                                {/* Package Info */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">Amount to Pay</p>
                                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatPrice(selectedPackage.price)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-600 dark:text-gray-400">Duration</p>
                                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                                {selectedPackage.duration_days} {selectedPackage.duration_days === 1 ? 'Day' : 'Days'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* M-Pesa Instructions */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                            <FaMobileAlt className="text-xl text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Pay via M-Pesa</h4>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">Send payment to Till Number</p>
                                        </div>
                                    </div>

                                    {/* Till Number */}
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 border-2 border-dashed border-gray-300 dark:border-gray-600">
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Till Number</p>
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-2xl font-bold text-green-600 dark:text-green-400 tracking-wider">
                                                {selectedPackage.payment_till_number || paymentInfo?.tillNumber}
                                            </p>
                                            <button
                                                onClick={() => handleCopyTillNumber(selectedPackage.payment_till_number || paymentInfo?.tillNumber)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all hover:scale-105 active:scale-95"
                                            >
                                                <FaCopy className="text-xs" />
                                                <span>Copy</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Steps */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
                                        <h5 className="font-semibold text-sm text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                            <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                                            Payment Steps
                                        </h5>
                                        <ol className="space-y-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
                                                <span>M-Pesa → Lipa na M-Pesa → Buy Goods</span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
                                                <span>Till Number: <strong>{selectedPackage.payment_till_number || paymentInfo?.tillNumber}</strong></span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
                                                <span>Amount: <strong>{formatPrice(selectedPackage.price)}</strong></span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
                                                <span>Enter PIN and confirm</span>
                                            </li>
                                        </ol>
                                    </div>

                                    {/* After Payment */}
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-3 border border-green-200 dark:border-green-800">
                                        <div className="flex items-start gap-2 mb-2">
                                            <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                                            <div>
                                                <h5 className="font-semibold text-sm text-gray-900 dark:text-white">After Payment</h5>
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Contact admin with M-Pesa code</p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-2 mt-2">
                                            <a 
                                                href="https://wa.me/254717325657"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-all hover:scale-105 shadow-sm"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                </svg>
                                                0717325657
                                            </a>
                                            <a 
                                                href="https://wa.me/254793026817"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-all hover:scale-105 shadow-sm"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                </svg>
                                                0793026817
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 sticky bottom-0 bg-white dark:bg-gray-800 pt-2 pb-2">
                                    <button
                                        onClick={handleCloseModal}
                                        className="flex-1 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold transition-all hover:scale-105 active:scale-95 shadow-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCloseModal}
                                        className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold transition-all hover:scale-105 active:scale-95 shadow-lg"
                                    >
                                        Got It
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PremiumPage;
